import { useCreatePresignedUrlMutation } from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { toFullS3Url } from '@/utils/toFullS3Url';
import { uploadFile } from '@/utils/uploadFile';
import { useCallback, useState } from 'react';

const logger = createLogger('useAdminUploadFile');

export function useAdminUploadFile() {
  const [createPresignedUrl] = useCreatePresignedUrlMutation();

  const [loading, setLoading] = useState(false);

  const adminUploadFile = useCallback(
    async (file: File) => {
      setLoading(true);
      try {
        logger.info('Creating presigned URL for mime type', file.type);
        const { data } = await createPresignedUrl({
          variables: { mimeType: file.type },
        });
        logger.info('Presigned URL created', data?.createPresignedUrl);
        const { url, key } = data?.createPresignedUrl || {};
        if (!url || !key) {
          logger.error('Failed to create presigned URL');
          throw new Error('Failed to create presigned URL');
        }
        await uploadFile({
          file,
          presignedUrl: url,
          onProgress: (progress) => {
            logger.info(`Uploading file: ${progress}%`);
          },
        });
        setLoading(false);
        return toFullS3Url(key);
      } catch (e) {
        setLoading(false);
        logger.error('Failed to upload file', e);
        throw e;
      }
    },
    [createPresignedUrl],
  );

  return [adminUploadFile, { loading }] as const;
}
